import { useLingui } from '@lingui/react/macro'
import { useDocChatAttachments } from 'features/knowledgeDocumentViewer/components/advancedSearch/contexts/DocChatAttachmentsContext'
import type { DocChatSubmittedQueryAttachment } from 'features/knowledgeDocumentViewer/types'
import { Image as ImageIcon } from 'lucide-react'
import React, { useEffect } from 'react'
import styled, { css } from 'styled-components'

import type { PreviewImageHandler } from './previewDocumentHandler'

const AttachmentRow = styled.div<{ $align: 'start' | 'end' }>`
  display: flex;
  flex-wrap: wrap;
  justify-content: ${({ $align }) =>
    $align === 'end' ? 'flex-end' : 'flex-start'};
  gap: 0.5rem;
`

const attachmentFrameStyles = css`
  display: block;
  padding: 0;
  border: 0.0625rem solid ${({ theme }) => theme.forecast.border2};
  border-radius: 0.5rem;
  background: ${({ theme }) => theme.forecast.cardGroupBackground};
  overflow: hidden;
  line-height: 0;
`

const AttachmentFrame = styled.span`
  ${attachmentFrameStyles}
`

const AttachmentButton = styled.button`
  ${attachmentFrameStyles}
  cursor: pointer;

  &:hover {
    border-color: ${({ theme }) => theme.forecast.secondaryText};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.forecast.primary};
    outline-offset: 2px;
  }
`

const AttachmentImage = styled.img`
  display: block;
  max-width: 11rem;
  max-height: 8rem;
  object-fit: cover;
`

const MissingAttachment = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  max-width: 12rem;
  padding: 0.25rem 0.5rem;
  border: 0.0625rem solid ${({ theme }) => theme.forecast.border2};
  border-radius: 0.375rem;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const MissingAttachmentName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

/**
 * Thumbnails resolve against in-memory attachments; a restored session shows the
 * filename alone because the image bytes are never persisted to search state.
 */
export function DocChatTurnAttachments({
  attachments,
  onPreviewImage,
  align = 'start',
}: {
  attachments?: DocChatSubmittedQueryAttachment[]
  onPreviewImage?: PreviewImageHandler
  /** 'end' matches the right-aligned user bubble in the chat view. */
  align?: 'start' | 'end'
}): React.ReactElement | null {
  const { t } = useLingui()
  const { sentAttachmentsById, requestAttachments } = useDocChatAttachments()

  // A restored transcript (tab switched away and back) has the ids but not the
  // bytes; pull them back out of the attachment store.
  useEffect(() => {
    const missing = (attachments ?? [])
      .filter((attachment) => !sentAttachmentsById.has(attachment.id))
      .map((attachment) => attachment.id)
    if (missing.length) requestAttachments(missing)
  }, [attachments, requestAttachments, sentAttachmentsById])

  if (!attachments?.length) return null

  return (
    <AttachmentRow $align={align}>
      {attachments.map((attachment) => {
        const sent = sentAttachmentsById.get(attachment.id)
        if (!sent) {
          return (
            <MissingAttachment key={attachment.id}>
              <ImageIcon size={12} aria-hidden='true' />
              <MissingAttachmentName title={attachment.name}>
                {attachment.name}
              </MissingAttachmentName>
            </MissingAttachment>
          )
        }
        const attachmentName = attachment.name
        const image = (
          <AttachmentImage src={sent.previewUrl} alt={attachmentName} />
        )
        if (!onPreviewImage) {
          return <AttachmentFrame key={attachment.id}>{image}</AttachmentFrame>
        }
        return (
          <AttachmentButton
            key={attachment.id}
            type='button'
            aria-label={t`View ${attachmentName}`}
            onClick={() =>
              onPreviewImage({
                name: sent.name,
                previewUrl: sent.previewUrl,
              })
            }>
            {image}
          </AttachmentButton>
        )
      })}
    </AttachmentRow>
  )
}
